/**
 * My Feedback — what I have said, and how far the owner has got with it.
 *
 * There used to be an "Assigned to me" list beside this one. It has moved into
 * REVIEWS, which is now the single place anything routed to you appears —
 * whether you are asked to decide it or only to see it. Two lists of nearly the
 * same rows, one of which could not be acted on, is how a verdict ends up
 * recorded against the wrong item.
 *
 * Opening a row replaces the list with the full record rather than a dialog: a
 * submission is a page's worth of material — answers, context and every verdict —
 * and it reads badly in a box.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { Tag } from 'sharedUi/Tag';
import SaasDataTable, { type SaasColumn } from '../../../components/common/saas-data-table';
import { ListCount } from '../../../components/common/list-count';
import { feedbackSubmissionService } from '../../../services/feedback-submission.service';
import type { FeedbackSubmission } from '../../../types/feedback-submission.types';
import { useToastApi } from '../../../hooks/use-toast-api';
import { statusSeverity } from '../feedback-display';
import { useComponentTypes } from '../../feedback-master/use-feedback-value-sets';
import { useSubmissionStatuses } from '../use-feedback-event-value-sets';
import SubmissionDetail from '../submission-detail';

const MyFeedbackTab = () => {
  const qc = useQueryClient();
  const { toast } = useToastApi();

  const [openId, setOpenId] = useState<string | null>(null);
  const [minePage, setMinePage] = useState(1);
  const [mineSize, setMineSize] = useState(10);
  /* Server-side, like every other list here: the repository already filtered on
     all three, only the /my endpoint never passed them through. Filtering in the
     browser would narrow the page in hand and leave the count describing a
     different list. */
  const [componentType, setComponentType] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const { options: componentTypeOptions } = useComponentTypes();
  const { options: statusOptions } = useSubmissionStatuses();

  const mineQ = useQuery({
    queryKey: ['feedback-my-submissions', minePage, mineSize, componentType, status, search],
    queryFn: () => feedbackSubmissionService.mine({
      page: minePage, page_size: mineSize,
      componentType: componentType || undefined,
      status: status || undefined,
      search: search || undefined,
    }),
    staleTime: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['feedback-my-submissions'] });
    qc.invalidateQueries({ queryKey: ['feedback-submissions'] });
    qc.invalidateQueries({ queryKey: ['feedback-reviews'] });
  };

  const columns: SaasColumn<FeedbackSubmission>[] = useMemo(() => [
    {
      field: 'feedbackKey', header: 'Feedback', hideable: false, style: { minWidth: 240 },
      body: (d) => (
        <div>
          <div className="font-mono text-xs font-semibold text-ink">
            {d.feedbackKey || '—'}
          </div>
          <div className="text-xs text-ink-muted">
            {d.module || '—'}
          </div>
        </div>
      ),
    },
    {
      field: 'component', header: 'Component', style: { minWidth: 180 },
      body: (d) => (
        <div>
          <Tag value={d.component?.componentType || '—'} severity="info" />
        </div>
      ),
    },
    {
      field: 'status', header: 'Status', align: 'center', style: { width: 150 },
      body: (d) => <Tag value={d.status} severity={statusSeverity(d.status) as never} />,
    },
    {
      field: 'submittedAt', header: 'Sent', style: { width: 170 },
      body: (d) => (
        <span className="text-xs text-ink-muted">
          {d.submittedAt ? new Date(d.submittedAt).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      field: 'actions', header: '', hideable: false, align: 'center', style: { width: 70 },
      body: (d) => (
        <Button aria-label="View" icon="pi pi-eye" text rounded tooltip="Open"
          onClick={() => setOpenId(d._id)} />
      ),
    },
  ], []);

  // The detail takes over the tab — no list behind it, no dialog on top of it.
  if (openId) {
    return (
      <>
        {toast}
        <SubmissionDetail
          submissionId={openId}
          onBack={() => setOpenId(null)}
          onChanged={invalidate}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {toast}


      <section className="rounded border border-gray-200 p-4 dark:border-gray-700">
        <h3 className="mb-3 text-sm font-semibold text-ink">
          Feedback I gave
          <span className="ml-2 text-xs font-normal text-gray-400">
            · Status shows how far the owner has got with it
          </span>
        </h3>
        <SaasDataTable
          tableId="feedback-mine"
          columns={columns}
          data={mineQ.data?.items ?? []}
          loading={mineQ.isFetching}
          dataKey="_id"
          emptyMessage="You have not given any feedback yet."
          totalRecords={mineQ.data?.total ?? 0}
          first={(minePage - 1) * mineSize}
          rows={mineSize}
          onPage={(e: { first: number; rows: number }) => {
            setMineSize(e.rows);
            setMinePage(Math.floor(e.first / e.rows) + 1);
          }}
          onSort={() => undefined}
          onFilter={() => undefined}
          testId="MyFeedback"
          globalSearch={search}
          onGlobalSearchChange={(v: string) => { setSearch(v); setMinePage(1); }}
          globalSearchPlaceholder="Search feedback key..."
          toolbarStart={
            <div className="flex flex-wrap items-center gap-2">
              <Dropdown
                value={componentType}
                options={[{ label: 'All Components', value: '' }, ...componentTypeOptions]}
                optionLabel="label" optionValue="value" placeholder="All Components"
                showClear={!!componentType} style={{ minWidth: 180 }}
                onChange={(e: { value: string }) => { setComponentType(e.value || ''); setMinePage(1); }}
                data-testid="MyFeedback.Filter.Component"
              />
              <Dropdown
                value={status}
                options={[{ label: 'All Statuses', value: '' }, ...statusOptions]}
                optionLabel="label" optionValue="value" placeholder="All Statuses"
                showClear={!!status} style={{ minWidth: 170 }}
                onChange={(e: { value: string }) => { setStatus(e.value || ''); setMinePage(1); }}
                data-testid="MyFeedback.Filter.Status"
              />
            </div>
          }
          toolbarEnd={<ListCount total={mineQ.data?.total} onRefresh={() => mineQ.refetch()}
            loading={mineQ.isFetching} testId="MyFeedback.Refresh" />}
        />
      </section>
    </div>
  );
};

export default MyFeedbackTab;
